// Globals
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { ImagesUploaderContainer, uploadCommonFile } from '@/modules/Upload';

// Store
import { useAppDispatch } from '@/store';
import { deletePropertyPhoto, uploadPropertyPhotos } from '../../feature';

// Components
import { Button, Spinner } from '@/ui';
import { StyledPictures } from './PicturesContainerStyles';

// Models
import { FileVariant, IFile } from '@/models/file.model';
import { FileType } from '@/models/uploads.model';

// Helpers
import { IMAGE_MIME_TYPES, MAX_IMAGE_SIZE } from '@/constants/files';
import { useGetPropertyPhotos } from '../../hooks';

type TPicturesContainerProps = {
  propertyId: string;
  disabled?: boolean;
};

const PicturesContainer = ({ propertyId, disabled = false }: TPicturesContainerProps) => {
  const { t } = useTranslation(['property']);
  const dispatch = useAppDispatch();
  const { floorPlan, isLoading, propertyPhotosInside, propertyPhotosOutside } = useGetPropertyPhotos(propertyId);

  const handleDelete = (photo: IFile) => {
    if (!photo?.id) return;
    dispatch(
      deletePropertyPhoto({
        file_id: photo.id,
        property_id: propertyId,
      }),
    );
  };

  const handleUpload = useCallback(
    (type: FileVariant) => async (_file: File, filesList: File[]) => {
      if (!filesList?.length) return;
      const promises = filesList.map((file) => dispatch(uploadCommonFile({ file, type: FileType.IMAGE })).unwrap());
      try {
        const uploadedFiles = await Promise.all(promises);
        const files =
          uploadedFiles.map((file, idx) => ({
            name: filesList[idx].name,
            link: file.asset_public_url,
          })) ?? [];

        return await dispatch(uploadPropertyPhotos({ files, property_id: propertyId, type }));
      } catch (error) {
        console.warn(error);
      }
    },
    [propertyId],
  );

  return (
    <Spinner spinning={isLoading}>
      <StyledPictures>
        <ImagesUploaderContainer
          onChange={handleUpload(FileVariant.PHOTO_INSIDE_PROPERTY)}
          title={t('photos_inside_title')}
          description={t('photos_drag_placeholder')}
          imagesList={propertyPhotosInside?.map((item) => ({
            name: item.file_name,
            id: item.PK,
            link: item.file_link,
          }))}
          acceptance={IMAGE_MIME_TYPES}
          maxImageSize={MAX_IMAGE_SIZE}
          multiple
          onDelete={handleDelete}
          type="drag"
          disabled={disabled}
        >
          <Button shape="round" type="primary">
            {t('button_select_pictures')}
          </Button>
        </ImagesUploaderContainer>
      </StyledPictures>
      <StyledPictures>
        <ImagesUploaderContainer
          onChange={handleUpload(FileVariant.PHOTO_OUTSIDE_PROPERTY)}
          title={t('photos_outside_title')}
          description={t('photos_drag_placeholder')}
          imagesList={propertyPhotosOutside?.map((item) => ({
            name: item.file_name,
            id: item.PK,
            link: item.file_link,
          }))}
          acceptance={IMAGE_MIME_TYPES}
          maxImageSize={MAX_IMAGE_SIZE}
          multiple
          onDelete={handleDelete}
          type="drag"
          disabled={disabled}
        >
          <Button shape="round" type="primary">
            {t('button_select_pictures')}
          </Button>
        </ImagesUploaderContainer>
      </StyledPictures>
      <StyledPictures>
        <ImagesUploaderContainer
          onChange={handleUpload(FileVariant.FLOOR_PLAN)}
          title={t('photos_floor_plan_title')}
          description={t('photos_drag_placeholder')}
          imagesList={floorPlan?.map((item) => ({
            name: item.file_name,
            id: item.PK,
            link: item.file_link,
          }))}
          acceptance={IMAGE_MIME_TYPES}
          maxImageSize={MAX_IMAGE_SIZE}
          multiple
          onDelete={handleDelete}
          type="drag"
          disabled={disabled}
        >
          <Button shape="round" type="primary">
            {t('button_select_pictures')}
          </Button>
        </ImagesUploaderContainer>
      </StyledPictures>
    </Spinner>
  );
};

export default memo(PicturesContainer);
